import type { Maybe } from "@entrilia/shared/types";
import { CalculationType } from "../../../../../../../../../../api/types/fieldConfigurationTypes";
import { ReportType } from "../../../../../../../../../../api/types/svTypes";
import type { DataSetIds } from "../../../../../../utilities/DimensionNames";

interface CalculationTypeCapabilities {
  supportsSubtotals: boolean;
  supportsFormatting: boolean;
  caption: string;
  /** The data set ids that the calculation type is available for */
  availableDataSetIds: (keyof typeof DataSetIds | (string & {}))[];
  /** The report types that the calculation type is available for */
  availableReportTypes: ReportType[];
  /** The calculation types that can be exchanged with the given calculation type */
  exchangeableWith: CalculationType[];
  canBeAppliedToMeasureColumn: boolean;
  sortable: boolean;
}

const allReportTypes: ReportType[] = Object.values(ReportType);

export const calculationTypeCapabilities: Record<CalculationType, CalculationTypeCapabilities> = {
  [CalculationType.CustomFormula]: {
    supportsSubtotals: true,
    supportsFormatting: true,
    caption: "Custom Formula",
    availableDataSetIds: ["GeneralAndMemo"],
    availableReportTypes: allReportTypes,
    exchangeableWith: [CalculationType.CustomFormula],
    canBeAppliedToMeasureColumn: true,
    sortable: true,
  },
  [CalculationType.PercentageOfTotal]: {
    supportsSubtotals: true,
    supportsFormatting: true,
    caption: "Percentage of Total",
    availableDataSetIds: ["GeneralAndMemo"],
    availableReportTypes: allReportTypes,
    exchangeableWith: [CalculationType.PercentageOfTotal],
    canBeAppliedToMeasureColumn: true,
    sortable: true,
  },
  [CalculationType.ConditionValue]: {
    supportsSubtotals: false,
    supportsFormatting: false,
    caption: "Condition Value",
    availableDataSetIds: ["GeneralAndMemo", "PortfolioMonitoring"],
    availableReportTypes: [ReportType.Tabular, ReportType.List],
    exchangeableWith: [CalculationType.ConditionValue],
    canBeAppliedToMeasureColumn: false,
    sortable: false,
  },
};

export const isSortable = (calculationType: Maybe<CalculationType>): boolean => {
  if (calculationType == null) {
    return false;
  }
  return calculationTypeCapabilities[calculationType].sortable;
};

/**
 * Get the calculation types that can be exchanged with the given calculation type
 * @param calculationType - The calculation type to get the exchangeable calculation types for
 * @returns The exchangeable calculation types. The result always contains self as the first item if it is defined.
 */
export const getExchangeableCalculationTypes = (calculationType: Maybe<CalculationType>): CalculationType[] => {
  if (calculationType == null) {
    return [];
  }
  return calculationTypeCapabilities[calculationType].exchangeableWith;
};

/**
 * Get the available calculation types for the given data set id and report type
 * @param dataSetId - The data set id to get the available calculation types for
 * @param reportType - The report type to get the available calculation types for
 * @returns The available calculation types
 */
export const getAvailableCalculationTypes = <T extends keyof typeof DataSetIds | (string & {})>(
  dataSetId: T | undefined,
  reportType: Maybe<ReportType>
): CalculationType[] => {
  return Object.entries(calculationTypeCapabilities)
    .filter(
      ([_, value]) =>
        value.availableDataSetIds.includes(dataSetId as T) &&
        !!reportType &&
        value.availableReportTypes.includes(reportType)
    )
    .map(([key]) => key as CalculationType);
};

/**
 * Check if the given calculation type supports formatting
 * @param calculationType - The calculation type to check if it supports formatting
 * @returns True if the calculation type supports formatting, false otherwise
 */
export const supportsFormatting = (calculationType: Maybe<CalculationType>): boolean => {
  if (calculationType == null) {
    return false;
  }
  return calculationTypeCapabilities[calculationType].supportsFormatting;
};

/**
 * Check if the given calculation type supports subtotals
 * @param calculationType - The calculation type to check if it supports subtotals
 * @returns True if the calculation type supports subtotals, false otherwise
 */
export const supportsSubtotals = (calculationType: Maybe<CalculationType>): boolean => {
  if (calculationType == null) {
    return false;
  }
  return calculationTypeCapabilities[calculationType].supportsSubtotals;
};

export const applicableToMeasureColumnCalculationTypes: CalculationType[] = Object.entries(calculationTypeCapabilities)
  .filter(([_, capabilities]) => capabilities.canBeAppliedToMeasureColumn)
  .map(([key]) => key as CalculationType);
